import React, { useState, useEffect, useRef, useMemo } from 'react';
import { GoogleMap, Polyline } from '@react-google-maps/api';
import { useGoogleMaps, GOOGLE_MAPS_MAP_ID } from '../../lib/googleMaps';
import AdvancedMarker from '../../components/maps/AdvancedMarker';
import RoutePolyline from '../../components/maps/RoutePolyline';
import api from '../../lib/api';
import { X, ArrowClockwise, Path as PathIcon, Clock, Truck } from '@phosphor-icons/react';

const MAP_STYLES = [
  { elementType: 'geometry', stylers: [{ color: '#f5f5f5' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#c9c9c9' }] },
];

const STATUS_BADGE = {
  in_progress: 'bg-[#002FA7] text-white',
  pickup_reached: 'bg-[#FFD700] text-[#7A5C00]',
  pickup_done: 'bg-[#FF8C00] text-white',
  drop_reached: 'bg-[#FFD700] text-[#7A5C00]',
};

function fmtAgo(iso) {
  if (!iso) return '—';
  const secs = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (secs < 10) return 'just now';
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  return `${Math.floor(secs / 3600)}h ${Math.floor((secs % 3600) / 60)}m ago`;
}

function fmtTime(iso) {
  if (!iso) return '-';
  return new Date(iso).toLocaleString('en-IN', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: 'short' });
}

function fmtETA(seconds) {
  if (seconds == null) return '—';
  const mins = Math.round(seconds / 60);
  if (mins < 60) return `${mins} min`;
  return `${Math.floor(mins / 60)}h ${mins % 60}m`;
}

/**
 * LiveTripMapModal — Full-screen overlay showing a single LIVE trip on Google Maps.
 *
 * Auto-refreshes every 10s so the pilot marker tracks the vehicle in
 * near-real-time. Shows pickup (green "S"), drop (red "E"), pilot
 * current position (pulsing blue dot), the planned road route (blue)
 * and the actually-travelled path (red) reconstructed from GPS points.
 *
 * ETA is computed from the pilot's *current* location to the next
 * waypoint:
 *   - status in {in_progress, pickup_reached}  → ETA to PICKUP
 *   - status in {pickup_done, drop_reached}    → ETA to DROP
 */
export default function LiveTripMapModal({ tripId, onClose }) {
  const { isLoaded } = useGoogleMaps();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [routeEtaSec, setRouteEtaSec] = useState(null);
  const [routeDistM, setRouteDistM] = useState(null);
  const mapRef = useRef(null);
  const centeredRef = useRef(false);

  const fetchTrip = async () => {
    try {
      const { data: d } = await api.get(`/trips/detail/${tripId}`);
      setData(d);
      setLastRefresh(new Date());
    } catch {
      /* keep last data on transient error */
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch + 10s polling
  useEffect(() => {
    fetchTrip();
    const id = setInterval(fetchTrip, 10000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tripId]);

  const trip = data?.trip;
  const gpsTracks = data?.gps_tracks || [];
  const routePath = useMemo(() => gpsTracks.map(p => ({ lat: p.lat, lng: p.lng })), [gpsTracks]);
  const currentPos = gpsTracks.length > 0 ? gpsTracks[gpsTracks.length - 1] : null;
  const lastGpsTime = currentPos?.timestamp;

  // Which leg are we showing ETA for?
  // - Pre-pickup (in_progress, pickup_reached): pilot → pickup
  // - Post-pickup (pickup_done, drop_reached, etc.): pilot → drop
  const showingPickupLeg = trip && ['in_progress', 'pickup_reached'].includes(trip.status);
  const legOrigin = currentPos ? { lat: currentPos.lat, lng: currentPos.lng } : null;
  const legDestination = trip
    ? (showingPickupLeg
        ? (trip.pickup_lat && trip.pickup_lng ? { lat: trip.pickup_lat, lng: trip.pickup_lng } : null)
        : (trip.drop_lat && trip.drop_lng ? { lat: trip.drop_lat, lng: trip.drop_lng } : null))
    : null;

  // Center / fit map once when first data arrives.
  useEffect(() => {
    if (centeredRef.current || !mapRef.current || !isLoaded || !trip) return;
    const bounds = new window.google.maps.LatLngBounds();
    if (trip.pickup_lat && trip.pickup_lng) bounds.extend({ lat: trip.pickup_lat, lng: trip.pickup_lng });
    if (trip.drop_lat && trip.drop_lng) bounds.extend({ lat: trip.drop_lat, lng: trip.drop_lng });
    if (currentPos) bounds.extend({ lat: currentPos.lat, lng: currentPos.lng });
    if (!bounds.isEmpty()) {
      mapRef.current.fitBounds(bounds, 80);
      centeredRef.current = true;
    }
  }, [isLoaded, trip, currentPos]);

  const center = trip && trip.pickup_lat && trip.drop_lat
    ? { lat: (trip.pickup_lat + trip.drop_lat) / 2, lng: (trip.pickup_lng + trip.drop_lng) / 2 }
    : { lat: 11.1085, lng: 77.3411 };

  if (loading && !trip) {
    return (
      <div className="fixed inset-0 z-[70] bg-black/70 flex items-center justify-center" data-testid="live-trip-map-loading">
        <div className="text-white text-sm uppercase tracking-widest">Loading live map…</div>
      </div>
    );
  }
  if (!trip) return null;

  const statusBadgeCls = STATUS_BADGE[trip.status] || 'bg-[#808080] text-white';
  const isGpsStale = lastGpsTime && (Date.now() - new Date(lastGpsTime).getTime()) > 5 * 60 * 1000;

  return (
    <div className="fixed inset-0 z-[70] bg-black/80 flex" data-testid="live-trip-map-modal">
      {/* MAP */}
      <div className="flex-1 relative">
        {isLoaded ? (
          <GoogleMap
            mapContainerStyle={{ width: '100%', height: '100%' }}
            center={center}
            zoom={12}
            options={{
              styles: MAP_STYLES,
              disableDefaultUI: false,
              zoomControl: true,
              mapTypeControl: false,
              streetViewControl: false,
              fullscreenControl: false,
              mapId: GOOGLE_MAPS_MAP_ID,
            }}
            onLoad={(m) => { mapRef.current = m; }}
          >
            {/* Planned road route — full trip pickup → drop, drawn blue */}
            {trip.pickup_lat && trip.pickup_lng && trip.drop_lat && trip.drop_lng && (
              <RoutePolyline
                origin={{ lat: trip.pickup_lat, lng: trip.pickup_lng }}
                destination={{ lat: trip.drop_lat, lng: trip.drop_lng }}
                usageKey="polyline_live_trip_modal"
                options={{ strokeColor: '#002FA7', strokeWeight: 5, strokeOpacity: 0.55 }}
              />
            )}

            {/* Live remaining leg — pilot → next waypoint (pickup or drop), bold green dashed */}
            {legOrigin && legDestination && (
              <RoutePolyline
                origin={legOrigin}
                destination={legDestination}
                usageKey="polyline_live_trip_modal"
                options={{
                  strokeColor: '#00C853',
                  strokeWeight: 6,
                  strokeOpacity: 0.95,
                  geodesic: false,
                  icons: [{
                    icon: { path: 'M 0,-1 0,1', strokeOpacity: 1, scale: 3 },
                    offset: '0',
                    repeat: '14px',
                  }],
                }}
                onRoute={(r) => {
                  setRouteEtaSec(r.durationSeconds);
                  setRouteDistM(r.distanceMeters);
                }}
              />
            )}

            {/* Actually travelled path (red, on top of planned) */}
            {routePath.length > 1 && (
              <Polyline
                path={routePath}
                options={{ strokeColor: '#FF3333', strokeWeight: 4, strokeOpacity: 0.9, zIndex: 3 }}
              />
            )}

            {/* Pickup marker (green "S") */}
            {trip.pickup_lat && trip.pickup_lng && (
              <AdvancedMarker
                position={{ lat: trip.pickup_lat, lng: trip.pickup_lng }}
                icon={{ color: '#00C853', label: 'P', size: 34 }}
                title={`Pickup: ${trip.pickup_supplier_name || ''}`}
              />
            )}

            {/* Drop marker (red "E") */}
            {trip.drop_lat && trip.drop_lng && (
              <AdvancedMarker
                position={{ lat: trip.drop_lat, lng: trip.drop_lng }}
                icon={{ color: '#FF3333', label: 'D', size: 34 }}
                title={`Drop: ${trip.drop_supplier_name || ''}`}
              />
            )}

            {/* Pilot live position — pulsing blue dot with vehicle number */}
            {currentPos && (
              <AdvancedMarker
                position={{ lat: currentPos.lat, lng: currentPos.lng }}
                zIndex={999}
                contentHTML={`
                  <div style="display:flex;flex-direction:column;align-items:center;gap:3px;font-family:system-ui,sans-serif;transform:translateY(-6px)">
                    <div style="background:#002FA7;color:#fff;padding:2px 8px;border-radius:10px;font-size:11px;font-weight:800;white-space:nowrap;box-shadow:0 2px 4px rgba(0,0,0,.3)">
                      ${(trip.pilot_name || '').replace(/[<>"']/g, '')}${trip.vehicle_number ? ' · ' + trip.vehicle_number.replace(/[<>"']/g, '') : ''}
                    </div>
                    <div style="position:relative;width:28px;height:28px">
                      <div style="position:absolute;inset:0;border-radius:50%;background:${isGpsStale ? '#808080' : '#002FA7'};border:3px solid #fff;box-shadow:0 0 0 2px ${isGpsStale ? '#808080' : '#002FA7'},0 2px 6px rgba(0,0,0,.4)"></div>
                      ${isGpsStale ? '' : `<div style="position:absolute;inset:-6px;border-radius:50%;background:#002FA7;opacity:.35;animation:lt-pulse 1.5s ease-out infinite"></div>`}
                    </div>
                  </div>
                  <style>@keyframes lt-pulse{0%{transform:scale(.6);opacity:.6}100%{transform:scale(1.6);opacity:0}}</style>
                `}
                title={`${trip.pilot_name} · ${Math.round(currentPos.speed || 0)} km/h · ${fmtAgo(lastGpsTime)}`}
              />
            )}
          </GoogleMap>
        ) : (
          <div className="h-full flex items-center justify-center text-white text-sm">Loading map…</div>
        )}

        {/* Map Legend overlay (bottom-left) */}
        <div className="absolute bottom-4 left-4 bg-white/95 backdrop-blur border border-[#E5E5E5] p-3 text-xs space-y-1 shadow-lg" data-testid="live-map-legend">
          <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-[#00C853]" />Pickup · {trip.pickup_supplier_name || '—'}</div>
          <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-[#FF3333]" />Drop · {trip.drop_supplier_name || '—'}</div>
          <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-[#002FA7]" />Pilot Current Location</div>
          <div className="flex items-center gap-2"><div className="w-8 h-1 bg-[#002FA7] opacity-60" />Planned Route (Full)</div>
          <div className="flex items-center gap-2"><div className="w-8 h-1 bg-[#00C853]" />Remaining Leg → {showingPickupLeg ? 'Pickup' : 'Drop'}</div>
          {routePath.length > 1 && (
            <div className="flex items-center gap-2"><div className="w-8 h-1 bg-[#FF3333]" />Travelled ({gpsTracks.length} pts)</div>
          )}
        </div>
      </div>

      {/* RIGHT INFO PANEL */}
      <div className="w-[360px] bg-white border-l border-[#E5E5E5] flex flex-col">
        {/* Header */}
        <div className="px-5 py-4 border-b border-[#E5E5E5] flex items-start justify-between bg-[#0A0A0A] text-white">
          <div>
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-[#00C853] animate-pulse" />
              <span className="text-[10px] uppercase tracking-widest font-bold text-[#00C853]">Live Trip</span>
            </div>
            <p className="font-mono text-sm mt-1">#{trip.id?.slice(0, 8)}</p>
          </div>
          <button
            onClick={onClose}
            className="text-white/70 hover:text-white p-1"
            data-testid="live-map-close-btn"
            title="Close"
          >
            <X size={22} weight="bold" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {/* Status pill */}
          <div className="flex items-center gap-2">
            <span className={`text-xs uppercase tracking-widest px-2.5 py-1 font-bold ${statusBadgeCls}`} data-testid="live-map-status-badge">
              {trip.status?.replace(/_/g, ' ')}
            </span>
            {trip.is_pooled_into && (
              <span className="text-[10px] uppercase tracking-widest px-2 py-1 bg-[#A855F7]/10 text-[#A855F7] font-bold">
                Pooled
              </span>
            )}
            {Array.isArray(trip.pooled_items) && trip.pooled_items.length > 0 && (
              <span className="text-[10px] uppercase tracking-widest px-2 py-1 bg-[#A855F7]/10 text-[#A855F7] font-bold">
                +{trip.pooled_items.length} pool
              </span>
            )}
          </div>

          {/* Route summary */}
          <div>
            <p className="text-[10px] uppercase tracking-widest font-bold text-[#666] mb-1">Route</p>
            <p className="text-sm font-bold leading-tight" data-testid="live-map-route">
              {trip.pickup_supplier_name} <span className="text-[#666]">→</span> {trip.drop_supplier_name}
            </p>
          </div>

          {/* Pilot + vehicle */}
          <div className="border-l-4 border-[#002FA7] pl-3 space-y-1">
            <p className="text-[10px] uppercase tracking-widest font-bold text-[#666]">Pilot</p>
            <p className="text-sm font-bold flex items-center gap-2">
              <Truck size={14} className="text-[#002FA7]" />
              {trip.pilot_name || '—'}
            </p>
            <p className="text-xs font-mono text-[#666]">{trip.vehicle_number || '—'}</p>
          </div>

          {/* ETA — to next leg */}
          <div className="grid grid-cols-2 gap-3" data-testid="live-map-eta-block">
            <div className="bg-[#00C853]/10 p-3 text-center">
              <Clock size={16} className="mx-auto text-[#00C853] mb-1" />
              <p className="text-lg font-black text-[#00C853]" data-testid="live-map-eta-value">
                {fmtETA(routeEtaSec)}
              </p>
              <p className="text-[10px] uppercase tracking-widest text-[#666] font-bold">
                ETA → {showingPickupLeg ? 'Pickup' : 'Drop'}
              </p>
            </div>
            <div className="bg-[#002FA7]/10 p-3 text-center">
              <PathIcon size={16} className="mx-auto text-[#002FA7] mb-1" />
              <p className="text-lg font-black text-[#002FA7]" data-testid="live-map-distance-value">
                {routeDistM != null ? `${(routeDistM / 1000).toFixed(1)} km` : '—'}
              </p>
              <p className="text-[10px] uppercase tracking-widest text-[#666] font-bold">Distance Left</p>
            </div>
          </div>

          {/* Current speed + last GPS */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-[#F7F7F7] p-3 text-center">
              <p className="text-lg font-black" data-testid="live-map-speed">
                {currentPos ? Math.round(currentPos.speed || 0) : '—'} <span className="text-xs font-normal text-[#666]">km/h</span>
              </p>
              <p className="text-[10px] uppercase tracking-widest text-[#666] font-bold">Current Speed</p>
            </div>
            <div className="bg-[#F7F7F7] p-3 text-center">
              <p className={`text-lg font-black ${isGpsStale ? 'text-[#FF3333]' : 'text-[#00C853]'}`} data-testid="live-map-last-gps">
                {fmtAgo(lastGpsTime)}
              </p>
              <p className="text-[10px] uppercase tracking-widest text-[#666] font-bold">Last GPS</p>
            </div>
          </div>

          {isGpsStale && (
            <div className="bg-[#FF3333]/10 border border-[#FF3333]/30 text-[#FF3333] p-2 text-xs" data-testid="live-map-stale-warning">
              ⚠ GPS hasn't updated in over 5 minutes. The pilot's device may be offline.
            </div>
          )}

          {/* Timeline */}
          <div className="border-l-4 border-[#FF8C00] pl-3 space-y-1">
            <p className="text-[10px] uppercase tracking-widest font-bold text-[#666]">Timeline</p>
            <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
              <span className="text-[#666]">Started</span><span>{fmtTime(trip.started_at)}</span>
              <span className="text-[#666]">Pickup Reached</span><span>{fmtTime(trip.pickup_reached_at)}</span>
              <span className="text-[#666]">Pickup Done</span><span>{fmtTime(trip.pickup_done_at)}</span>
              <span className="text-[#666]">Drop Reached</span><span>{fmtTime(trip.drop_reached_at)}</span>
            </div>
          </div>

          {/* Item / qty */}
          {(trip.item || (Array.isArray(trip.items) && trip.items.length > 0)) && (
            <div className="border-l-4 border-[#A855F7] pl-3 space-y-1">
              <p className="text-[10px] uppercase tracking-widest font-bold text-[#666]">Cargo</p>
              {Array.isArray(trip.items) && trip.items.length > 0 ? (
                trip.items.map((it, i) => (
                  <p key={i} className="text-xs">{it.name} · <span className="font-bold">{it.qty} {it.uom || ''}</span></p>
                ))
              ) : (
                <p className="text-xs">{trip.item} · <span className="font-bold">{trip.qty || 0} {trip.uom || ''}</span></p>
              )}
            </div>
          )}
        </div>

        {/* Footer — manual refresh */}
        <div className="px-5 py-3 border-t border-[#E5E5E5] flex items-center justify-between bg-[#F7F7F7]">
          <span className="text-[10px] text-[#666]">
            Auto-refresh 10s · last {lastRefresh ? fmtAgo(lastRefresh.toISOString()) : '—'}
          </span>
          <button
            onClick={fetchTrip}
            className="text-[10px] uppercase tracking-widest font-bold bg-[#002FA7] text-white px-3 py-1.5 hover:bg-[#002FA7]/90 flex items-center gap-1"
            data-testid="live-map-refresh-btn"
          >
            <ArrowClockwise size={12} weight="bold" /> Refresh
          </button>
        </div>
      </div>
    </div>
  );
}
